import { Injectable } from '@angular/core';
import { AttributionService } from '@products/services/attribution.service';
import {
  FundAttributionDownloadableGridData,
  FundAttributionDownloadableTableFormat,
  FundAttributionGridData,
  FundAttributionOtherGridData,
  FundAttributionState,
  FundAttributionTimePeriod,
  FundAttributionGridDataTypes,
  FundAttributionTotalRegionGridData,
  FundAttributionTotalSectorGridData,
  FundAttributionPeriodDates,
  FundAttributionCustomDataFetch,
} from '@products/types/fund-attribution.type';
import { FundId, ProductDetailConfiguration } from '@types';
import { Logger } from '@utils/logger';
import { Subject, Observable, forkJoin } from 'rxjs';
import fundAttributionQuery from '@graphql/fund-attribution/fund-attribution.graphql';
import {
  Attribution,
  AttributionCountry,
  AttributionSector,
} from '@products/models/attribution';
import { TranslateService } from '@shared/translate/translate.service';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { catchError, map, publishLast, refCount, take } from 'rxjs/operators';
import { ProfileService } from '@services/profile.service';
import dayjs, { Dayjs } from 'dayjs';
import { getPreviousQuarters } from '@utils/text/date-utils';
import { API_DATE_FORMAT, FP } from '@utils/app.constants';
import { SiteConfigService } from '@services/site-config.service';
import cloneDeep from 'lodash/cloneDeep';

const logger = Logger.getLogger('FundAttributionService');

@Injectable({
  providedIn: 'root',
})
export class FundAttributionService {
  private state: FundAttributionState = {
    fundName: null,
    bmName: null,
    selectedAsOfDateStd: null,
    selectedGridDataType: 'sector',
    periodDates: [],
    calendarYears: [],
    timePeriods: null,
    showCurrencyAttribution: false, // default false, as per story criteria.
    validSignIn: false,
  };
  private fundAttributionState$: Subject<FundAttributionState>;
  private attributionCache: Observable<Attribution[]>;
  private asOfDateQuarterly: string;
  private asOfDateYearly: string;
  private currentFundId: FundId;

  constructor(
    private attributionService: AttributionService,
    private translateService: TranslateService,
    private caveatService: CaveatService,
    private profileService: ProfileService,
    private siteConfigService: SiteConfigService
  ) {
    this.fundAttributionState$ = new Subject();
  }

  /**
   * Returns updated state as Observable.
   */
  getFundAttributionState$(): Observable<FundAttributionState> {
    return this.fundAttributionState$.asObservable();
  }

  /**
   * Call attribution service & perform formatting of attribution data.
   * Handle bootstrapping of the attribution table structure.
   * @param productDetailConfiguration the configuration received on page detail load
   * @param fundAttributionCustomDataFetch the custom data fetch details
   */
  populate(
    productDetailConfiguration: ProductDetailConfiguration,
    fundAttributionCustomDataFetch?: FundAttributionCustomDataFetch
  ): void {
    this.getAttributionData(
      productDetailConfiguration,
      fundAttributionCustomDataFetch
    ).subscribe((attributionData) => {
      logger.debug('Attribution data: ', attributionData);

      // Assign action values as selected.
      if (fundAttributionCustomDataFetch) {
        this.state.selectedAsOfDateStd =
          fundAttributionCustomDataFetch.selectedAsOfDateStd;
        this.state.selectedGridDataType =
          fundAttributionCustomDataFetch.selectedGridDataType;
        this.state.showCurrencyAttribution =
          fundAttributionCustomDataFetch.showCurrencyAttribution;
      }

      this.handleAttributionData(attributionData);
    });
  }

  /**
   * Fetch atttribution data from pds service.
   * @param productDetailConfiguration the configuration received on page detail load
   * @param fundAttributionCustomDataFetch the custom data fetch details
   */
  getAttributionData(
    productDetailConfiguration: ProductDetailConfiguration,
    fundAttributionCustomDataFetch?: FundAttributionCustomDataFetch
  ): Observable<Attribution[]> {
    // Return attribution cache on initial load (to this and product-detail component), while with new record on new date selection.
    // If there is a want for initial load data, then return the cache.
    // NGC-11404: Check fundId as well, to have fresh API call when fund page navigates.
    if (
      (!this.attributionCache &&
        !fundAttributionCustomDataFetch?.selectedAsOfDateStd) ||
      fundAttributionCustomDataFetch?.selectedAsOfDateStd ||
      (this.attributionCache && // Cache exists but fundId gets change - condition for fund navigation.
        this.currentFundId !== productDetailConfiguration.fundId)
    ) {
      // Save current fundId to make an API call when fund page navigates.
      this.currentFundId = productDetailConfiguration.fundId;

      const { asOfDateQuarterly, asOfDateYearly } = this.getDefaultAsOfDates(
        fundAttributionCustomDataFetch
      );

      // Save quarterly and yearly as of date accordingly.
      // To be used later in different places.
      this.asOfDateQuarterly = asOfDateQuarterly;
      this.asOfDateYearly = asOfDateYearly;

      this.attributionCache = this.attributionService
        .register(fundAttributionQuery, {
          fundId: productDetailConfiguration.fundId,
          asofdatequarterly: Number(
            dayjs(asOfDateQuarterly).format('YYYYMMDD')
          ),
          asofdateyearly: Number(dayjs(asOfDateYearly).format('YYYYMMDD')),
        })
        .pipe(take(1), publishLast(), refCount());
    }

    return this.attributionCache;
  }

  /**
   * Calculate and return default quarterly and yearly as of date.
   * @param fundAttributionCustomDataFetch the custom data fetch details
   */
  private getDefaultAsOfDates(
    fundAttributionCustomDataFetch: FundAttributionCustomDataFetch
  ): {
    asOfDateQuarterly: string;
    asOfDateYearly: string;
  } {
    if (fundAttributionCustomDataFetch) {
      return {
        asOfDateQuarterly:
          fundAttributionCustomDataFetch.targetView !==
          FundAttributionTimePeriod.CALENDAR_YEAR
            ? fundAttributionCustomDataFetch.selectedAsOfDateStd
            : this.asOfDateQuarterly,
        asOfDateYearly:
          fundAttributionCustomDataFetch.targetView ===
          FundAttributionTimePeriod.CALENDAR_YEAR
            ? fundAttributionCustomDataFetch.selectedAsOfDateStd
            : this.asOfDateYearly,
      };
    } else {
      const todaysDateMoment: Dayjs = dayjs();
      const cutOffDateQuarterlyMoment: Dayjs = dayjs()
        .startOf('quarter')
        .add(15, 'days');
      const cutOffDateYearlyMoment: Dayjs = dayjs()
        .startOf('year')
        .add(15, 'days');

      let asOfDateQuarterlyMoment: Dayjs;
      let asOfDateYearlyMoment: Dayjs;

      // ********** LOGIC: asOfDateQuarterly/asOfDateYearly **********
      // Ref. PDS: Last quarter data will be visible post 2nd fortnight of current quarter start date.

      // QUARTERLY RULE: If today's date is before start of 2nd fortnight of current month,
      // Then, the last quarter would be 2 quarters back.
      // Else, it would be 1 quarter before the current one.
      if (todaysDateMoment.isBefore(cutOffDateQuarterlyMoment)) {
        asOfDateQuarterlyMoment = cloneDeep(todaysDateMoment).subtract(
          2,
          'quarter'
        );
      } else {
        asOfDateQuarterlyMoment = cloneDeep(todaysDateMoment).subtract(
          1,
          'quarter'
        );
      }

      // YEARLY RULE: If today's date is before start of 2nd fortnight of current year January month,
      // Then, the last calendar year would be 2 yrs back.
      // Else, it would be 1 year before the current one.
      if (todaysDateMoment.isBefore(cutOffDateYearlyMoment)) {
        asOfDateYearlyMoment = cloneDeep(todaysDateMoment).subtract(2, 'year');
      } else {
        asOfDateYearlyMoment = cloneDeep(todaysDateMoment).subtract(1, 'year');
      }

      return {
        asOfDateQuarterly: asOfDateQuarterlyMoment
          .endOf('quarter')
          .format('YYYY-MM-DD'),
        asOfDateYearly: asOfDateYearlyMoment.endOf('year').format('YYYY-MM-DD'),
      };
    }
  }

  /**
   * Extract and format the attribution data as per business.
   * @param attributionData attribution data from feed.
   */
  private handleAttributionData(attributionData: Attribution[]): void {
    const [quarterlyAttributionData, yearlyAttributionData] = attributionData;

    if (
      quarterlyAttributionData.country.length ||
      quarterlyAttributionData.sector.length ||
      yearlyAttributionData.country.length ||
      yearlyAttributionData.sector.length
    ) {
      this.profileService.getUserProfile$().subscribe(
        (profile) => {
          if (profile.isLoggedIn && profile.profileInfo.role === FP) {
            // Assign sign-in profile as valid.
            this.state.validSignIn = true;

            // Create and assign period dates for dropdown.
            this.state.periodDates = this.getPeriodDates();
            this.state.calendarYears = this.getCalendarYears();

            // Now, get attribution data as per timeperiods.
            this.state.timePeriods = {
              quarterly: this.formatAttributionData(
                this.getFilteredAttributionDataByTimePeriod(
                  quarterlyAttributionData,
                  FundAttributionTimePeriod.QUARTERLY
                )
              ),
              twelveMonthRolling: this.formatAttributionData(
                this.getFilteredAttributionDataByTimePeriod(
                  quarterlyAttributionData,
                  FundAttributionTimePeriod.TWELVE_MONTH_ROLLING
                )
              ),
              ytd: this.formatAttributionData(
                this.getFilteredAttributionDataByTimePeriod(
                  quarterlyAttributionData,
                  FundAttributionTimePeriod.YTD
                )
              ),
              calendarYear: this.formatAttributionData(
                this.getFilteredAttributionDataByTimePeriod(
                  yearlyAttributionData,
                  FundAttributionTimePeriod.CALENDAR_YEAR
                )
              ),
            };

            // Assign other necessary stuffs to state.
            this.state.fundName =
              quarterlyAttributionData.country[0]?.fundName ||
              quarterlyAttributionData.sector[0]?.fundName;
            this.state.bmName =
              quarterlyAttributionData.country[0]?.benchmark ||
              quarterlyAttributionData.sector[0]?.benchmark;
          } else {
            this.state.validSignIn = false; // Need to assign it in else, as profile can change and its an observable.
          }

          this.state.hasLoadedFullFeed = true;
          this.fundAttributionState$.next(this.state);
        },
        (error) => {
          logger.error(
            `Error in retrieving profile info in attribution: ${error}`
          );

          this.state.validSignIn = false;
          this.state.hasLoadedFullFeed = true;
          this.fundAttributionState$.next(this.state);
        }
      );
    } else {
      this.state.hasLoadedFullFeed = false;
      this.fundAttributionState$.next(this.state);
    }
  }

  /**
   * Retrieve last 5 years quarters and send the periods as 'FundAttributionPeriodDates' list.
   */
  private getPeriodDates(): FundAttributionPeriodDates[] {
    const periods: FundAttributionPeriodDates[] = [];

    // Retreive last 5 years quarters and then push it to period list.
    getPreviousQuarters(
      20,
      dayjs().format('YYYY-MM-DD'),
      API_DATE_FORMAT,
      this.siteConfigService.getPdsLanguage(),
      'YYYY-MM-DD'
    )?.forEach((quarter) => {
      periods.push({
        asOfDate: dayjs(quarter).format('MM/DD/YYYY'),
        asOfDateStd: quarter,
        year: quarter.split('-')[0], // Only, interested in 'yyyy' format from 'yyyy-mm-dd'.
      });
    });

    return periods;
  }

  /**
   * Retrieve calendar list for last 5 years.
   */
  private getCalendarYears(): FundAttributionPeriodDates[] {
    const years: FundAttributionPeriodDates[] = [];
    let lastCalendarYear: number;
    let numberOfCalendarYear: number;

    const todaysDateMoment: Dayjs = dayjs();
    const cutOffDateMoment: Dayjs = dayjs().startOf('year').add(15, 'days');

    // ********** LOGIC: number of calendar years may be 4 or 5 **********
    // Ref. PDS: Last quarter data will be visible post start of 2nd fortnight of current quarter start date.
    // So, if today's date is before start of 2nd fortnight of current year January month,
    // Then, the last calendar year would be 2 yrs back.
    // Else, it would be 1 year before the current one.
    if (todaysDateMoment.isBefore(cutOffDateMoment)) {
      lastCalendarYear = cloneDeep(todaysDateMoment).subtract(2, 'year').year();
      numberOfCalendarYear = 4;
    } else {
      lastCalendarYear = cloneDeep(todaysDateMoment).subtract(1, 'year').year();
      numberOfCalendarYear = 5;
    }

    for (let year = 0; year < numberOfCalendarYear; year++) {
      const asOfDateYear: number = lastCalendarYear - year;

      years.push({
        asOfDate: `12-31-${asOfDateYear}`,
        asOfDateStd: `${asOfDateYear}-12-31`,
        year: String(asOfDateYear),
      });
    }

    return years;
  }

  /**
   * Filter & group the attribution data as per time period.
   * @param attributionData mapped service response
   * @param timePeriod target time period
   */
  private getFilteredAttributionDataByTimePeriod(
    attributionData: Attribution,
    timePeriod: FundAttributionTimePeriod
  ): Attribution {
    switch (timePeriod) {
      case FundAttributionTimePeriod.QUARTERLY:
        return {
          country: attributionData.country.filter(
            (attributionCountry) => attributionCountry.numberOfMonths === '3'
          ),
          sector: attributionData.sector.filter(
            (attributionSector) => attributionSector.numberOfMonths === '3'
          ),
        };
      case FundAttributionTimePeriod.TWELVE_MONTH_ROLLING: {
        return {
          country: attributionData.country.filter(
            (attributionCountry) => attributionCountry.numberOfMonths === '12'
          ),
          sector: attributionData.sector.filter(
            (attributionSector) => attributionSector.numberOfMonths === '12'
          ),
        };
      }
      case FundAttributionTimePeriod.YTD: {
        return {
          country: attributionData.country.filter((attributionCountry) =>
            this.asOfDateQuarterly
              .split('-')[1]
              .includes(attributionCountry.numberOfMonths)
          ),
          sector: attributionData.sector.filter((attributionSector) =>
            this.asOfDateQuarterly
              .split('-')[1]
              .includes(attributionSector.numberOfMonths)
          ),
        };
      }
      case FundAttributionTimePeriod.CALENDAR_YEAR:
        return {
          country: attributionData.country.filter(
            (attributionCountry) =>
              attributionCountry.toDateStd.includes('12-31') &&
              attributionCountry.numberOfMonths === '12'
          ),
          sector: attributionData.sector.filter(
            (attributionSector) =>
              attributionSector.toDateStd.includes('12-31') &&
              attributionSector.numberOfMonths === '12'
          ),
        };
    }
  }

  /**
   * Format the available timeperiod data into regions and sectors individually.
   * @param attributionData response from feed
   */
  private formatAttributionData(
    attributionData: Attribution
  ): FundAttributionGridDataTypes {
    return {
      regionsTotalData: this.formatCountryAttributionData(
        attributionData.country
      ),
      sectorsTotalData: this.formatSectorAttributionData(
        attributionData.sector
      ),
    };
  }

  /**
   * Format attribution country data to target data structure.
   * @param attributionCountries countries data for given timeperiod
   */
  private formatCountryAttributionData(
    attributionCountries: AttributionCountry[]
  ): FundAttributionTotalRegionGridData & FundAttributionOtherGridData {
    let regionsTotalData: FundAttributionTotalRegionGridData &
      FundAttributionOtherGridData;

    // Step 1: Extract "level 0" i.e., 'total' record.
    const matchedAttributionCountry: AttributionCountry = attributionCountries.find(
      (attributionCountry) => attributionCountry.levelNumber === '0'
    );

    if (matchedAttributionCountry) {
      regionsTotalData = {
        totalData: this.formatAttributionGridData(matchedAttributionCountry),
        regions: [],
        asOfDate: matchedAttributionCountry.toDate,
        asOfDateStd: matchedAttributionCountry.toDateStd,
        levelNumber: matchedAttributionCountry.levelNumber,
        numberOfMonths: matchedAttributionCountry.numberOfMonths,
      };

      // Step 2: Iterate raw data again, but match all "level 1" records i.e., all 'region'.
      attributionCountries
        .filter((attributionCountry) => attributionCountry.levelNumber === '1')
        // Step 3: Push all matched 'region' records to the 'total' record.
        .forEach((attributionCountry) => {
          regionsTotalData.regions.push({
            regionName: attributionCountry.region,
            regionData: this.formatAttributionGridData(attributionCountry),
            // Step 4: Iterate raw data again, but match all "level 2" records i.e., all 'country'.
            countries: attributionCountries
              .filter(
                (attributionCountryObj) =>
                  attributionCountryObj.levelNumber === '2' &&
                  attributionCountryObj.region === attributionCountry.region
              )
              // Step 5: Push all the matched 'country' records to the 'region' record.
              .map((attributionCountryObj) => {
                return {
                  countryName: attributionCountryObj.country,
                  countryData: this.formatAttributionGridData(
                    attributionCountryObj
                  ),
                  asOfDate: attributionCountryObj.toDate,
                  asOfDateStd: attributionCountryObj.toDateStd,
                  levelNumber: attributionCountryObj.levelNumber,
                  numberOfMonths: attributionCountryObj.numberOfMonths,
                };
              }),
            asOfDate: attributionCountry.toDate,
            asOfDateStd: attributionCountry.toDateStd,
            levelNumber: attributionCountry.levelNumber,
            numberOfMonths: attributionCountry.numberOfMonths,
          });
        });

      // Step 6: Sort every 'total' record with 'region' and 'country' alphabetically.
      //         But, make "Cash & Other Net Assets" to occur last as per business.
      regionsTotalData.regions.sort((a, b) => {
        if (a.regionName === 'Cash & Other Net Assets') {
          return 1;
        } else if (b.regionName === 'Cash & Other Net Assets') {
          return -1;
        }
        return a.regionName.localeCompare(b.regionName);
      });
      regionsTotalData.regions.forEach((region) =>
        region.countries.sort((a, b) =>
          a.countryName.localeCompare(b.countryName)
        )
      );
    }

    return regionsTotalData;
  }

  /**
   * Format attribution sector data to target data structure.
   * @param attributionSectors sectors data for given timeperiod
   */
  private formatSectorAttributionData(
    attributionSectors: AttributionSector[]
  ): FundAttributionTotalSectorGridData & FundAttributionOtherGridData {
    let sectorsTotalData: FundAttributionTotalSectorGridData &
      FundAttributionOtherGridData;

    // Step 1: Extract "level 0" i.e., 'total' record.
    const matchedAttributionSector: AttributionSector = attributionSectors.find(
      (attributionSector) => attributionSector.levelNumber === '0'
    );

    if (matchedAttributionSector) {
      sectorsTotalData = {
        totalData: this.formatAttributionGridData(matchedAttributionSector),
        sectors: [],
        asOfDate: matchedAttributionSector.toDate,
        asOfDateStd: matchedAttributionSector.toDateStd,
        levelNumber: matchedAttributionSector.levelNumber,
        numberOfMonths: matchedAttributionSector.numberOfMonths,
      };

      // Step 2: Iterate raw data again, but match all "level 1" records i.e., all 'sector'.
      attributionSectors
        .filter((attributionSector) => attributionSector.levelNumber === '1')
        // Step 3: Push all matched 'sector' records to the 'total' record.
        .forEach((attributionSector) => {
          sectorsTotalData.sectors.push({
            sectorName: attributionSector.sector,
            sectorData: this.formatAttributionGridData(attributionSector),
            // Step 4: Iterate raw data again, but match all "level 2" records i.e., all 'industry'.
            industries: attributionSectors
              .filter(
                (attributionIndustry) =>
                  attributionIndustry.levelNumber === '2' &&
                  attributionIndustry.sector === attributionSector.sector
              )
              // Step 5: Push all the matched 'industry' records to the 'sector' record.
              .map((attributionIndustry) => {
                return {
                  industryName: attributionIndustry.industry,
                  industryData: this.formatAttributionGridData(
                    attributionIndustry
                  ),
                  asOfDate: attributionIndustry.toDate,
                  asOfDateStd: attributionIndustry.toDateStd,
                  levelNumber: attributionIndustry.levelNumber,
                  numberOfMonths: attributionIndustry.numberOfMonths,
                };
              }),
            asOfDate: attributionSector.toDate,
            asOfDateStd: attributionSector.toDateStd,
            levelNumber: attributionSector.levelNumber,
            numberOfMonths: attributionSector.numberOfMonths,
          });
        });

      // Step 6: Sort every 'total' record with 'sector' and 'industry' alphabetically.
      //         But, make "Cash & Other Net Assets" to occur last as per business.
      sectorsTotalData.sectors.sort((a, b) => {
        if (a.sectorName === 'Cash & Other Net Assets') {
          return 1;
        } else if (b.sectorName === 'Cash & Other Net Assets') {
          return -1;
        }
        return a.sectorName.localeCompare(b.sectorName);
      });
      sectorsTotalData.sectors.forEach((sector) =>
        sector.industries.sort((a, b) =>
          a.industryName.localeCompare(b.industryName)
        )
      );
    }

    return sectorsTotalData;
  }

  /**
   * Extract the grid data from country/sector and return the grid data object accordingly.
   * @param gridData country/sector feed object
   */
  private formatAttributionGridData(
    gridData: AttributionCountry | AttributionSector
  ): FundAttributionGridData {
    return {
      portfolio: {
        averageWeight: gridData.portfolioAverageWeight,
        totalReturn: gridData.portfolioTotalReturn,
        contributionToReturn: gridData.portfolioContributionToReturn,
      },
      benchmark: {
        averageWeight: gridData.bmAverageWeight,
        totalReturn: gridData.bmTotalReturn,
        contributionToReturn: gridData.bmContributionToReturn,
      },
      variation: {
        averageWeight: gridData.variationAverageWeight,
        totalReturn: gridData.variationTotalReturn,
        contributionToReturn: gridData.variationContributionToReturn,
      },
      currency: {
        allocationEffect: gridData.allocationEffectLocal,
        selectionEffect: gridData.selectionEffectLocal,
        interactionEffect: gridData.interactionEffectLocal,
        totalEffect: gridData.totalEffectLocal,
        totalCurrencyEffect: gridData.totalCurrencyEffect,
      },
      attribution: {
        allocationEffect: gridData.allocationEffect,
        selectionEffect: gridData.selectionEffect,
        interactionEffect: gridData.interactionEffect,
        totalEffect: gridData.totalEffect,
      },
    };
  }

  /**
   * Handle download action.
   * Convert table records into excel format and initiate download.
   * @param downloadableGridData sector/country data for selected time period
   */
  triggerDownloadAction(
    downloadableGridData: FundAttributionDownloadableGridData
  ): void {
    const mappedHeader: Map<string, string> = this.mapDataHeader();
    let mappedSectorFullRecords: string[][] = [];
    let mappedCountryFullRecords: string[][] = [];

    // Map sector data accordingly.
    if (downloadableGridData.selectedSectorTotalData) {
      const mappedSectorBody: FundAttributionDownloadableTableFormat[] = this.mapSectorDataBody(
        downloadableGridData.selectedSectorTotalData
      );

      mappedSectorFullRecords = [
        Object.keys(mappedSectorBody[0]).map((data) =>
          mappedHeader.get(data) === ''
            ? ''
            : this.translateService.instant(mappedHeader.get(data))
        ),
      ];

      mappedSectorBody.forEach((data) => {
        mappedSectorFullRecords.push(Object.values(data));
      });
    }

    // Map country data accordingly.
    if (downloadableGridData.selectedCountryTotalData) {
      const mappedCountryBody: FundAttributionDownloadableTableFormat[] = this.mapCountryDataBody(
        downloadableGridData.selectedCountryTotalData
      );

      mappedCountryFullRecords = [
        Object.keys(mappedCountryBody[0]).map((data) =>
          mappedHeader.get(data) === ''
            ? ''
            : this.translateService.instant(mappedHeader.get(data))
        ),
      ];

      mappedCountryBody.forEach((data) => {
        mappedCountryFullRecords.push(Object.values(data));
      });
    }

    // Map other supporting data.
    const fundName = [];

    fundName.push([downloadableGridData.fundName || '']);

    forkJoin([
      this.getProximalCaveat('FundAttributionExcelProximalBottom'),
      this.getProximalCaveat('FundAttributionExcelProximalTop'),
    ]).subscribe((data) => {
      const [bottomCaveat, topCaveat]: [string[][], string[][]] = data;
      // Finally, export all mapped data.
      this.exportToExcel(
        fundName,
        mappedSectorFullRecords,
        mappedCountryFullRecords,
        topCaveat,
        bottomCaveat
      );
    });
  }

  /**
   * get the proximal caveats for a particular placement
   */
  getProximalCaveat(placement: string): Observable<string[][]> {
    return this.caveatService.getProximals$(placement).pipe(
      take(1),
      map((caveatContext) => {
        const caveat = [];
        const caveatData = caveatContext.map((data) => data.split('\n'));

        caveatData[0]?.map((data: any) => {
          let trimedData = data.replace(/(<([^>]+)>)/gi, '');
          trimedData = this.replaceWithAmpersand(trimedData);

          if (trimedData.length > 0) {
            caveat.push([trimedData]);
          }
        });

        return caveat;
      }),
      catchError(() => {
        return [];
      })
    );
  }

  replaceWithAmpersand(data: string): string {
    const regex = /&amp;/gi;
    return data.replace(regex, '&');
  }

  /**
   * Return mapped header for corresponding column data.
   */
  private mapDataHeader(): Map<string, string> {
    const mappedHeader = new Map<string, string>();

    mappedHeader.set('classification', ''); // Intentionally left blank, as in UI, its the place for expand/collapse all toggle.
    mappedHeader.set(
      'portfolioAverageWeight',
      'products.attribution-portfolio-average-weight'
    );
    mappedHeader.set(
      'portfolioTotalReturn',
      'products.attribution-portfolio-total-return'
    );
    mappedHeader.set(
      'portfolioContributionToReturn',
      'products.attribution-portfolio-contribution-to-return'
    );
    mappedHeader.set(
      'benchmarkAverageWeight',
      'products.attribution-bm-average-weight'
    );
    mappedHeader.set(
      'benchmarkTotalReturn',
      'products.attribution-bm-total-return'
    );
    mappedHeader.set(
      'benchmarkContributionToReturn',
      'products.attribution-bm-contribution-to-return'
    );
    mappedHeader.set(
      'variationAverageWeight',
      'products.attribution-variation-average-weight'
    );
    mappedHeader.set(
      'variationTotalReturn',
      'products.attribution-variation-total-return'
    );
    mappedHeader.set(
      'variationContributionToReturn',
      'products.attribution-variation-contribution-to-return'
    );
    mappedHeader.set(
      'currencyAllocationEffect',
      'products.attribution-currency-allocation-effect'
    );
    mappedHeader.set(
      'currencySelectionEffect',
      'products.attribution-currency-selection-effect'
    );
    mappedHeader.set(
      'currencyInteractionEffect',
      'products.attribution-currency-interaction-effect'
    );
    mappedHeader.set(
      'currencyTotalEffect',
      'products.attribution-currency-total-effect'
    );
    mappedHeader.set(
      'currencyTotalCurrencyEffect',
      'products.attribution-currency-total-currency-effect'
    );
    mappedHeader.set(
      'attributionAllocationEffect',
      'products.attribution-attribution-allocation-effect'
    );
    mappedHeader.set(
      'attributionSelectionEffect',
      'products.attribution-attribution-selection-effect'
    );
    mappedHeader.set(
      'attributionInteractionEffect',
      'products.attribution-attribution-interaction-effect'
    );
    mappedHeader.set(
      'attributionTotalEffect',
      'products.attribution-attribution-total-effect'
    );

    return mappedHeader;
  }

  /**
   * Return mapped sector data for table body.
   * @param totalSectorGridData sector data for selected time period
   */
  private mapSectorDataBody(
    totalSectorGridData: FundAttributionTotalSectorGridData
  ): FundAttributionDownloadableTableFormat[] {
    const mappedBody: FundAttributionDownloadableTableFormat[] = [];

    // Map total of the sector data.
    mappedBody.push({
      classification: this.translateService.instant(
        'products.attribution-sector-industry'
      ),
      portfolioAverageWeight:
        totalSectorGridData.totalData.portfolio.averageWeight,
      portfolioTotalReturn: totalSectorGridData.totalData.portfolio.totalReturn,
      portfolioContributionToReturn:
        totalSectorGridData.totalData.portfolio.contributionToReturn,
      benchmarkAverageWeight:
        totalSectorGridData.totalData.benchmark.averageWeight,
      benchmarkTotalReturn: totalSectorGridData.totalData.benchmark.totalReturn,
      benchmarkContributionToReturn:
        totalSectorGridData.totalData.benchmark.contributionToReturn,
      variationAverageWeight:
        totalSectorGridData.totalData.variation.averageWeight,
      variationTotalReturn: totalSectorGridData.totalData.variation.totalReturn,
      variationContributionToReturn:
        totalSectorGridData.totalData.variation.contributionToReturn,
      currencyAllocationEffect:
        totalSectorGridData.totalData.currency.allocationEffect,
      currencySelectionEffect:
        totalSectorGridData.totalData.currency.selectionEffect,
      currencyInteractionEffect:
        totalSectorGridData.totalData.currency.interactionEffect,
      currencyTotalEffect: totalSectorGridData.totalData.currency.totalEffect,
      currencyTotalCurrencyEffect:
        totalSectorGridData.totalData.currency.totalCurrencyEffect,
      attributionAllocationEffect:
        totalSectorGridData.totalData.attribution.allocationEffect,
      attributionSelectionEffect:
        totalSectorGridData.totalData.attribution.selectionEffect,
      attributionInteractionEffect:
        totalSectorGridData.totalData.attribution.interactionEffect,
      attributionTotalEffect:
        totalSectorGridData.totalData.attribution.totalEffect,
    });

    // This will add an empty row to excel after 'total'.
    mappedBody.push({
      classification: '',
    });

    totalSectorGridData.sectors.forEach((sector) => {
      mappedBody.push({
        classification: sector.sectorName,
        portfolioAverageWeight: sector.sectorData.portfolio.averageWeight,
        portfolioTotalReturn: sector.sectorData.portfolio.totalReturn,
        portfolioContributionToReturn:
          sector.sectorData.portfolio.contributionToReturn,
        benchmarkAverageWeight: sector.sectorData.benchmark.averageWeight,
        benchmarkTotalReturn: sector.sectorData.benchmark.totalReturn,
        benchmarkContributionToReturn:
          sector.sectorData.benchmark.contributionToReturn,
        variationAverageWeight: sector.sectorData.variation.averageWeight,
        variationTotalReturn: sector.sectorData.variation.totalReturn,
        variationContributionToReturn:
          sector.sectorData.variation.contributionToReturn,
        currencyAllocationEffect: sector.sectorData.currency.allocationEffect,
        currencySelectionEffect: sector.sectorData.currency.selectionEffect,
        currencyInteractionEffect: sector.sectorData.currency.interactionEffect,
        currencyTotalEffect: sector.sectorData.currency.totalEffect,
        currencyTotalCurrencyEffect:
          sector.sectorData.currency.totalCurrencyEffect,
        attributionAllocationEffect:
          sector.sectorData.attribution.allocationEffect,
        attributionSelectionEffect:
          sector.sectorData.attribution.selectionEffect,
        attributionInteractionEffect:
          sector.sectorData.attribution.interactionEffect,
        attributionTotalEffect: sector.sectorData.attribution.totalEffect,
      });

      sector.industries.forEach((industry) => {
        mappedBody.push({
          classification: industry.industryName,
          portfolioAverageWeight: industry.industryData.portfolio.averageWeight,
          portfolioTotalReturn: industry.industryData.portfolio.totalReturn,
          portfolioContributionToReturn:
            industry.industryData.portfolio.contributionToReturn,
          benchmarkAverageWeight: industry.industryData.benchmark.averageWeight,
          benchmarkTotalReturn: industry.industryData.benchmark.totalReturn,
          benchmarkContributionToReturn:
            industry.industryData.benchmark.contributionToReturn,
          variationAverageWeight: industry.industryData.variation.averageWeight,
          variationTotalReturn: industry.industryData.variation.totalReturn,
          variationContributionToReturn:
            industry.industryData.variation.contributionToReturn,
          currencyAllocationEffect:
            industry.industryData.currency.allocationEffect,
          currencySelectionEffect:
            industry.industryData.currency.selectionEffect,
          currencyInteractionEffect:
            industry.industryData.currency.interactionEffect,
          currencyTotalEffect: industry.industryData.currency.totalEffect,
          currencyTotalCurrencyEffect:
            industry.industryData.currency.totalCurrencyEffect,
          attributionAllocationEffect:
            industry.industryData.attribution.allocationEffect,
          attributionSelectionEffect:
            industry.industryData.attribution.selectionEffect,
          attributionInteractionEffect:
            industry.industryData.attribution.interactionEffect,
          attributionTotalEffect: industry.industryData.attribution.totalEffect,
        });
      });

      // This will add an empty row to excel after every 'grouped' row.
      mappedBody.push({
        classification: '',
      });
    });

    return mappedBody;
  }

  /**
   * Return mapped country data for table body.
   * @param totalRegionGridData country data for selected time period
   */
  private mapCountryDataBody(
    totalRegionGridData: FundAttributionTotalRegionGridData
  ): FundAttributionDownloadableTableFormat[] {
    const mappedBody: FundAttributionDownloadableTableFormat[] = [];

    // Map total of the sector data.
    mappedBody.push({
      classification: this.translateService.instant(
        'products.attribution-region-country'
      ),
      portfolioAverageWeight:
        totalRegionGridData.totalData.portfolio.averageWeight,
      portfolioTotalReturn: totalRegionGridData.totalData.portfolio.totalReturn,
      portfolioContributionToReturn:
        totalRegionGridData.totalData.portfolio.contributionToReturn,
      benchmarkAverageWeight:
        totalRegionGridData.totalData.benchmark.averageWeight,
      benchmarkTotalReturn: totalRegionGridData.totalData.benchmark.totalReturn,
      benchmarkContributionToReturn:
        totalRegionGridData.totalData.benchmark.contributionToReturn,
      variationAverageWeight:
        totalRegionGridData.totalData.variation.averageWeight,
      variationTotalReturn: totalRegionGridData.totalData.variation.totalReturn,
      variationContributionToReturn:
        totalRegionGridData.totalData.variation.contributionToReturn,
      currencyAllocationEffect:
        totalRegionGridData.totalData.currency.allocationEffect,
      currencySelectionEffect:
        totalRegionGridData.totalData.currency.selectionEffect,
      currencyInteractionEffect:
        totalRegionGridData.totalData.currency.interactionEffect,
      currencyTotalEffect: totalRegionGridData.totalData.currency.totalEffect,
      currencyTotalCurrencyEffect:
        totalRegionGridData.totalData.currency.totalCurrencyEffect,
      attributionAllocationEffect:
        totalRegionGridData.totalData.attribution.allocationEffect,
      attributionSelectionEffect:
        totalRegionGridData.totalData.attribution.selectionEffect,
      attributionInteractionEffect:
        totalRegionGridData.totalData.attribution.interactionEffect,
      attributionTotalEffect:
        totalRegionGridData.totalData.attribution.totalEffect,
    });

    mappedBody.push({
      classification: '',
    });

    totalRegionGridData.regions.forEach((region) => {
      mappedBody.push({
        classification: region.regionName,
        portfolioAverageWeight: region.regionData.portfolio.averageWeight,
        portfolioTotalReturn: region.regionData.portfolio.totalReturn,
        portfolioContributionToReturn:
          region.regionData.portfolio.contributionToReturn,
        benchmarkAverageWeight: region.regionData.benchmark.averageWeight,
        benchmarkTotalReturn: region.regionData.benchmark.totalReturn,
        benchmarkContributionToReturn:
          region.regionData.benchmark.contributionToReturn,
        variationAverageWeight: region.regionData.variation.averageWeight,
        variationTotalReturn: region.regionData.variation.totalReturn,
        variationContributionToReturn:
          region.regionData.variation.contributionToReturn,
        currencyAllocationEffect: region.regionData.currency.allocationEffect,
        currencySelectionEffect: region.regionData.currency.selectionEffect,
        currencyInteractionEffect: region.regionData.currency.interactionEffect,
        currencyTotalEffect: region.regionData.currency.totalEffect,
        currencyTotalCurrencyEffect:
          region.regionData.currency.totalCurrencyEffect,
        attributionAllocationEffect:
          region.regionData.attribution.allocationEffect,
        attributionSelectionEffect:
          region.regionData.attribution.selectionEffect,
        attributionInteractionEffect:
          region.regionData.attribution.interactionEffect,
        attributionTotalEffect: region.regionData.attribution.totalEffect,
      });

      region.countries.forEach((country) => {
        mappedBody.push({
          classification: country.countryName,
          portfolioAverageWeight: country.countryData.portfolio.averageWeight,
          portfolioTotalReturn: country.countryData.portfolio.totalReturn,
          portfolioContributionToReturn:
            country.countryData.portfolio.contributionToReturn,
          benchmarkAverageWeight: country.countryData.benchmark.averageWeight,
          benchmarkTotalReturn: country.countryData.benchmark.totalReturn,
          benchmarkContributionToReturn:
            country.countryData.benchmark.contributionToReturn,
          variationAverageWeight: country.countryData.variation.averageWeight,
          variationTotalReturn: country.countryData.variation.totalReturn,
          variationContributionToReturn:
            country.countryData.variation.contributionToReturn,
          currencyAllocationEffect:
            country.countryData.currency.allocationEffect,
          currencySelectionEffect: country.countryData.currency.selectionEffect,
          currencyInteractionEffect:
            country.countryData.currency.interactionEffect,
          currencyTotalEffect: country.countryData.currency.totalEffect,
          currencyTotalCurrencyEffect:
            country.countryData.currency.totalCurrencyEffect,
          attributionAllocationEffect:
            country.countryData.attribution.allocationEffect,
          attributionSelectionEffect:
            country.countryData.attribution.selectionEffect,
          attributionInteractionEffect:
            country.countryData.attribution.interactionEffect,
          attributionTotalEffect: country.countryData.attribution.totalEffect,
        });
      });

      mappedBody.push({
        classification: '',
      });
    });

    return mappedBody;
  }

  /**
   * Export the table data to excel and auto download.
   * @param fundName fund name
   * @param sectorData full sector record for excel
   * @param countryData full country record for excel
   * @param topCaveat top caveat description
   * @param bottomCaveat botton caveat description
   */
  private exportToExcel(
    fundName: string[][],
    sectorData: string[][],
    countryData: string[][],
    topCaveat: string[][],
    bottomCaveat: string[][]
  ): void {
    import('xlsx').then((xlsx) => {
      const workBook = xlsx.utils.book_new();

      [sectorData, countryData].forEach((data, index) => {
        // If sector/country grid type data exists, then create a tab in excel accordingly.
        if (data.length) {
          const workSheet = xlsx.utils.aoa_to_sheet(topCaveat);

          workSheet['!cols'] = this.fitToColumn(fundName);

          xlsx.utils.sheet_add_aoa(workSheet, fundName, {
            origin: 'A' + (topCaveat.length + 2),
          });

          xlsx.utils.sheet_add_aoa(workSheet, data, {
            origin: 'A' + (topCaveat.length + fundName.length + 4),
          });

          xlsx.utils.sheet_add_aoa(workSheet, bottomCaveat, {
            origin:
              'A' +
              (sectorData.length + 6 + topCaveat.length + fundName.length),
          });

          const worksheetName: string =
            index === 0
              ? this.translateService.instant(
                  'products.attribution-excel-tabname-sector'
                )
              : this.translateService.instant(
                  'products.attribution-excel-tabname-country'
                );

          // Check if, worksheet name is greater than "31 chars", then, save it from code crash.
          // By not providing the name and let 'xlsx' assign default sheet name.
          worksheetName.length > 31
            ? xlsx.utils.book_append_sheet(workBook, workSheet)
            : xlsx.utils.book_append_sheet(workBook, workSheet, worksheetName);
        }
      });

      xlsx.writeFile(
        workBook,
        this.translateService.instant('products.attribution-excel-filename')
      );
    });
  }

  /**
   * Get maximum character of each column.
   * @param data full record
   */
  private fitToColumn(data: string[][]): { wch: number }[] {
    return data[0].map((a, i) => ({
      wch: Math.max(...data.map((a2) => a2[i].toString().length)),
    }));
  }
}
